/**
 * ZAXIA Staff - Housekeeping Page
 * Room status and cleaning management
 *
 * Phase P2.1-5: Staff Dashboard Shell (Placeholder)
 * Phase P2.2-10: Green Stay eco events integration
 * Phase P2.3-1: Housekeeping Daily Service Planner
 */

import { useState } from 'react';
import { useHousekeepingPlanner } from '@/hooks';
import { estimateTotalDuration, formatDuration } from '@/services/housekeepingPlanner';
import type { ServiceMode, HousekeepingTask, TaskPriority } from '@/services/housekeepingPlanner';
import type { TaskOrigin } from '@/api';
import { updateTaskStatus } from '@/api';

// Match the seeded rooms: 101-105, 201-205, 301-305, 401-405
const HOTEL_ROOMS = [
  ...Array.from({ length: 5 }, (_, i) => `${101 + i}`), // 101-105
  ...Array.from({ length: 5 }, (_, i) => `${201 + i}`), // 201-205
  ...Array.from({ length: 5 }, (_, i) => `${301 + i}`), // 301-305
  ...Array.from({ length: 5 }, (_, i) => `${401 + i}`), // 401-405
];

export function HousekeepingPage() {
  // Room selection state for filtering tasks
  const [selectedRoom, setSelectedRoom] = useState<string | null>(null);

  // Use the planner hook
  const {
    plan,
    loading,
    error,
    selectedView,
    refreshing,
    switchToToday,
    switchToTomorrow,
    refresh
  } = useHousekeepingPlanner(HOTEL_ROOMS);

  /**
   * Phase P2.5-1: Handle task status updates
   * Updates task status and syncs with PMS room status
   */
  const handleTaskStatusChange = async (taskId: string, newStatus: 'IN_PROGRESS' | 'COMPLETED') => {
    try {
      console.log(`[Housekeeping] Updating task ${taskId} to ${newStatus}...`);
      const result = await updateTaskStatus(taskId, newStatus);
      console.log('[Housekeeping] Task updated:', result);
      // Refresh the plan to show updated status and room grid
      console.log('[Housekeeping] Refreshing page...');
      await refresh();
      console.log('[Housekeeping] ✓ Refresh complete');
    } catch (err) {
      console.error('[Housekeeping] Failed to update task status:', err);
      alert(`Failed to update task: ${err instanceof Error ? err.message : 'Unknown error'}`);
    }
  };

  // Get service mode for a specific room from the plan
  const getRoomServiceMode = (roomNumber: string): ServiceMode => {
    if (!plan) return 'FULL_CLEAN';

    const roomPlan = plan.rooms.find(r => r.roomNumber === roomNumber);
    return roomPlan?.serviceMode || 'FULL_CLEAN';
  };

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-white">Housekeeping</h1>
          <p className="text-slate-400 mt-1">
            {plan
              ? `Daily service plan for ${selectedView === 'today' ? 'today' : 'tomorrow'} • ${plan.tasks.filter(t => t.type !== 'SKIPPED' && t.status !== 'COMPLETED' && t.status !== 'CANCELLED' && t.status !== 'BLOCKED').length} pending tasks • ${formatDuration(estimateTotalDuration(plan.tasks.filter(t => t.type !== 'SKIPPED' && t.status !== 'COMPLETED' && t.status !== 'CANCELLED' && t.status !== 'BLOCKED')))} total`
              : 'Loading daily service plan...'
            }
          </p>
        </div>
        <div className="flex gap-2">
          {/* Today/Tomorrow Toggle */}
          <button
            type="button"
            onClick={switchToToday}
            className={`px-4 py-2 text-sm font-medium rounded-lg border transition-colors ${
              selectedView === 'today'
                ? 'bg-blue-600 hover:bg-blue-700 text-white border-blue-500'
                : 'bg-slate-800 hover:bg-slate-700 text-white border-slate-600'
            }`}
          >
            Today
          </button>
          <button
            type="button"
            onClick={switchToTomorrow}
            className={`px-4 py-2 text-sm font-medium rounded-lg border transition-colors ${
              selectedView === 'tomorrow'
                ? 'bg-blue-600 hover:bg-blue-700 text-white border-blue-500'
                : 'bg-slate-800 hover:bg-slate-700 text-white border-slate-600'
            }`}
          >
            Tomorrow
          </button>

          {/* Refresh Button */}
          <button
            type="button"
            onClick={refresh}
            disabled={refreshing}
            className="px-4 py-2 bg-slate-800 hover:bg-slate-700 disabled:opacity-50 disabled:cursor-not-allowed text-white text-sm font-medium rounded-lg border border-slate-600 transition-colors"
          >
            {refreshing ? '↻ Refreshing...' : '↻ Refresh'}
          </button>
        </div>
      </div>

      {/* Error State */}
      {error && (
        <div className="bg-red-500/20 border border-red-500/30 rounded-lg p-4">
          <p className="text-red-400 text-sm">Error: {error}</p>
        </div>
      )}

      {/* Loading State */}
      {loading && !plan && (
        <div className="bg-slate-800 border border-slate-700 rounded-lg p-8 text-center">
          <p className="text-slate-400">Generating daily service plan...</p>
        </div>
      )}

      {/* Tomorrow Info Banner */}
      {plan && plan.isTomorrow && (
        <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg px-4 py-3 flex items-center gap-3">
          <span className="text-blue-400 text-lg">ℹ️</span>
          <p className="text-blue-300 text-sm font-medium">
            Tomorrow&apos;s plan — informative only. Shows eco preferences for planning. No tasks can be performed.
          </p>
        </div>
      )}

      {/* Workload Summary */}
      {plan && (
        <div className={`grid gap-4 ${plan.workload.checkoutToday > 0 ? 'grid-cols-2 md:grid-cols-5' : 'grid-cols-2 md:grid-cols-4'}`}>
          <WorkloadCard
            label="Full Cleaning"
            count={plan.workload.fullClean}
            color="blue"
            icon="✓"
          />
          <WorkloadCard
            label="Towels Only"
            count={plan.workload.towelsOnly}
            color="yellow"
            icon="~"
          />
          <WorkloadCard
            label="Skip Cleaning"
            count={plan.workload.skip}
            color="green"
            icon="✕"
          />
          {plan.workload.checkoutToday > 0 && (
            <WorkloadCard
              label={plan.isTomorrow ? 'Checkout Day' : 'Checkout Today'}
              count={plan.workload.checkoutToday}
              color="orange"
              icon="🚪"
            />
          )}
          <WorkloadCard
            label="Out of Order"
            count={plan.workload.outOfOrder}
            color="red"
            icon="!"
          />
        </div>
      )}

      {/* Room Grid */}
      {plan && (
        <Panel title={`Room Status • ${plan.rooms.length} rooms`}>
          <div className="grid grid-cols-5 md:grid-cols-10 gap-2">
            {HOTEL_ROOMS.map((roomNumber) => {
              const serviceMode = getRoomServiceMode(roomNumber);
              const roomPlan = plan.rooms.find(r => r.roomNumber === roomNumber);
              return (
                <RoomCell
                  key={roomNumber}
                  room={roomNumber}
                  serviceMode={serviceMode}
                  notes={roomPlan?.notes?.join(' • ')}
                  source={roomPlan?.source}
                  isTomorrow={plan?.isTomorrow}
                  isSelected={selectedRoom === roomNumber}
                  onClick={() => setSelectedRoom(selectedRoom === roomNumber ? null : roomNumber)}
                />
              );
            })}
          </div>
          <div className="mt-4 flex items-center gap-6 text-xs flex-wrap">
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded bg-blue-500" />
              <span className="text-slate-400">Full Cleaning ({plan.workload.fullClean})</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded bg-amber-500" />
              <span className="text-slate-400">Towels Only ({plan.workload.towelsOnly})</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded bg-emerald-500" />
              <span className="text-slate-400">Skip Cleaning 🌿 ({plan.workload.skip})</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded bg-slate-600" />
              <span className="text-slate-400">Already Clean</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded bg-orange-500" />
              <span className="text-slate-400">{plan.isTomorrow ? 'Checkout Day' : 'Checkout Today'} ({plan.workload.checkoutToday})</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded bg-red-500" />
              <span className="text-slate-400">Out of Order ({plan.workload.outOfOrder})</span>
            </div>
          </div>
        </Panel>
      )}

      {/* Task Panels */}
      {plan && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Pending Tasks */}
          <Panel title={
            selectedRoom
              ? `Room ${selectedRoom} Tasks • ${plan.tasks.filter(t => t.roomNumber === selectedRoom && t.type !== 'SKIPPED' && t.status !== 'COMPLETED' && t.status !== 'CANCELLED' && t.status !== 'BLOCKED').length} tasks`
              : `Pending Tasks • ${plan.tasks.filter(t => t.type !== 'SKIPPED' && t.status !== 'COMPLETED' && t.status !== 'CANCELLED' && t.status !== 'BLOCKED').length} tasks`
          }>
            {selectedRoom && (
              <div className="mb-3 flex items-center gap-2">
                <span className="text-xs text-slate-400">Filtering by room:</span>
                <span className="text-xs font-semibold text-white bg-slate-700 px-2 py-1 rounded">{selectedRoom}</span>
                <button
                  onClick={() => setSelectedRoom(null)}
                  className="text-xs text-slate-400 hover:text-white underline"
                >
                  Clear filter
                </button>
              </div>
            )}
            {(() => {
              // Filter out completed and cancelled tasks - only show active work
              const activeTasks = plan.tasks.filter(t =>
                t.status !== 'COMPLETED' &&
                t.status !== 'CANCELLED'
              );

              const filteredTasks = selectedRoom
                ? activeTasks.filter(t => t.roomNumber === selectedRoom)
                : activeTasks;

              return filteredTasks.length === 0 ? (
                <PlaceholderContent>
                  {selectedRoom
                    ? `No tasks for room ${selectedRoom}`
                    : `No pending housekeeping tasks for ${selectedView}`}
                </PlaceholderContent>
              ) : (
                <div className="space-y-2 max-h-96 overflow-y-auto">
                  {filteredTasks.map((task) => (
                    <TaskCard key={task.id} task={task} onStatusChange={plan?.isTomorrow ? undefined : handleTaskStatusChange} />
                  ))}
                </div>
              );
            })()}
          </Panel>

          {/* Workload Summary */}
          <Panel title="Workload Summary">
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <span className="text-slate-400 text-sm">Total Tasks</span>
                <span className="text-white font-semibold">
                  {plan.tasks.filter(t => t.status !== 'COMPLETED' && t.status !== 'CANCELLED').length}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-slate-400 text-sm">Estimated Duration</span>
                <span className="text-white font-semibold">{formatDuration(estimateTotalDuration(plan.tasks.filter(t => t.type !== 'SKIPPED' && t.status !== 'COMPLETED' && t.status !== 'CANCELLED' && t.status !== 'BLOCKED')))}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-slate-400 text-sm">Rooms Requiring Service</span>
                <span className="text-white font-semibold">
                  {plan.workload.fullClean + plan.workload.towelsOnly}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-slate-400 text-sm">Rooms Skipped</span>
                <span className="text-white font-semibold">{plan.workload.skip}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-slate-400 text-sm">Out of Service</span>
                <span className="text-white font-semibold">{plan.workload.outOfOrder}</span>
              </div>
              <div className="pt-3 border-t border-slate-700">
                <div className="flex items-center justify-between">
                  <span className="text-slate-400 text-sm">Generated At</span>
                  <span className="text-white text-sm">
                    {new Date(plan.generatedAt).toLocaleTimeString()}
                  </span>
                </div>
                <div className="flex items-center justify-between mt-2">
                  <span className="text-slate-400 text-sm">Auto-refresh</span>
                  <span className="text-white text-sm">Every 60s</span>
                </div>
              </div>
            </div>
          </Panel>
        </div>
      )}
    </div>
  );
}

// =============================================================================
// HELPER COMPONENTS
// =============================================================================

interface WorkloadCardProps {
  label: string;
  count: number;
  color: 'green' | 'blue' | 'red' | 'yellow' | 'gray' | 'orange';
  icon: string;
}

function WorkloadCard({ label, count, color, icon }: WorkloadCardProps) {
  const bgColors = {
    green: 'bg-emerald-500/20 border-emerald-500/30',
    blue: 'bg-blue-500/20 border-blue-500/30',
    red: 'bg-red-500/20 border-red-500/30',
    yellow: 'bg-yellow-500/20 border-yellow-500/30',
    gray: 'bg-slate-500/20 border-slate-500/30',
    orange: 'bg-orange-500/20 border-orange-500/30',
  };

  const textColors = {
    green: 'text-emerald-400',
    blue: 'text-blue-400',
    red: 'text-red-400',
    yellow: 'text-yellow-400',
    gray: 'text-slate-400',
    orange: 'text-orange-400',
  };

  return (
    <div className={`${bgColors[color]} border rounded-lg p-4 text-center`}>
      <div className="flex items-center justify-center gap-2 mb-2">
        <span className={`text-xl font-bold ${textColors[color]}`}>{icon}</span>
        <p className={`text-2xl font-semibold ${textColors[color]}`}>{count}</p>
      </div>
      <p className="text-slate-400 text-xs">{label}</p>
    </div>
  );
}

interface TaskCardProps {
  task: HousekeepingTask;
  onStatusChange?: (taskId: string, newStatus: 'IN_PROGRESS' | 'COMPLETED') => void;
}

function TaskCard({ task, onStatusChange }: TaskCardProps) {
  const getPriorityColor = (priority: TaskPriority) => {
    switch (priority) {
      case 'HIGH':
        return 'bg-red-500/20 border-red-500/30 text-red-400';
      case 'MEDIUM':
        return 'bg-yellow-500/20 border-yellow-500/30 text-yellow-400';
      case 'LOW':
      default:
        return 'bg-green-500/20 border-green-500/30 text-green-400';
    }
  };

  /**
   * Phase P2.5-1: PMS Origin Indicators
   * Show task origin with icons and colors (Deliverable #8)
   */
  const getOriginBadge = (origin?: string) => {
    switch (origin) {
      case 'PMS_CHECKOUT':
        return { icon: '🚪', label: 'Checkout', color: 'bg-red-500/20 border-red-500/30 text-red-400' };
      case 'MAINTENANCE':
        return { icon: '🔧', label: 'Maintenance', color: 'bg-orange-500/20 border-orange-500/30 text-orange-400' };
      case 'GREEN_STAY':
        return { icon: '🌿', label: 'Eco', color: 'bg-emerald-500/20 border-emerald-500/30 text-emerald-400' };
      case 'GUEST_REQUEST':
        return { icon: '📋', label: 'Request', color: 'bg-blue-500/20 border-blue-500/30 text-blue-400' };
      default:
        return { icon: '✓', label: 'Standard', color: 'bg-slate-500/20 border-slate-500/30 text-slate-400' };
    }
  };

  // Check if task has origin data (Phase P2.5-1 new fields)
  const taskOrigin = (task as any).origin as TaskOrigin | undefined;
  const originBadge = taskOrigin ? getOriginBadge(taskOrigin) : null;

  return (
    <div className="bg-slate-700/50 border border-slate-600 rounded-lg p-3 hover:bg-slate-700 transition-colors">
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <span className="text-white font-semibold">Room {task.roomNumber}</span>
            {task.guestName && (
              <>
                <span className="text-slate-400 text-sm">•</span>
                <span className="text-slate-300 text-sm">{task.guestName}</span>
              </>
            )}
            {/* Phase P2.5-1: Origin Badge (Deliverable #8) */}
            {originBadge && (
              <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium border ${originBadge.color}`}>
                {originBadge.icon} {originBadge.label}
              </span>
            )}
          </div>
          <div className="flex items-center gap-2 mb-1">
            <span className="text-slate-400 text-sm">
              {task.type === 'SKIPPED' ? '🚫 Cleaning Skipped' : task.type === 'FULL_CLEAN' ? 'Full Cleaning' : 'Towels Only'}
            </span>
            {task.greenStayEnabled && <span className="text-emerald-400 text-sm">🌿 Green Stay</span>}
          </div>
          <div className="flex items-center gap-2 text-xs text-slate-400">
            {task.type !== 'SKIPPED' && (
              <>
                <span>⏱ {task.estimatedDuration} min</span>
                <span>•</span>
              </>
            )}
            <span>{new Date(task.date).toLocaleDateString()}</span>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <div className={`px-2 py-1 rounded text-xs font-medium border whitespace-nowrap ${getPriorityColor(task.priority)}`}>
            {task.priority}
          </div>
        </div>
      </div>

      {/* Phase P2.5-1: Task Action Buttons */}
      {onStatusChange && task.status !== 'COMPLETED' && task.type !== 'SKIPPED' && (
        <div className="mt-3 pt-3 border-t border-slate-600 flex gap-2">
          {task.status === 'PENDING' && (
            <button
              onClick={() => onStatusChange(task.id, 'IN_PROGRESS')}
              className="flex-1 px-3 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg transition-colors"
            >
              🧹 Start Cleaning
            </button>
          )}
          {task.status === 'IN_PROGRESS' && (
            <button
              onClick={() => onStatusChange(task.id, 'COMPLETED')}
              className="flex-1 px-3 py-2 bg-green-600 hover:bg-green-700 text-white text-sm font-medium rounded-lg transition-colors"
            >
              ✅ Complete Cleaning
            </button>
          )}
        </div>
      )}
    </div>
  );
}

interface PanelProps {
  title: string;
  children: React.ReactNode;
}

function Panel({ title, children }: PanelProps) {
  return (
    <div className="bg-slate-800 border border-slate-700 rounded-lg">
      <div className="px-5 py-4 border-b border-slate-700">
        <h2 className="text-white font-medium">{title}</h2>
      </div>
      <div className="p-5">{children}</div>
    </div>
  );
}

interface RoomCellProps {
  room: string;
  serviceMode: ServiceMode;
  notes?: string;
  source?: string;
  isTomorrow?: boolean;
  isSelected?: boolean;
  onClick?: () => void;
}

function RoomCell({ room, serviceMode, notes, source, isTomorrow, isSelected, onClick }: RoomCellProps) {
  const getServiceColor = () => {
    switch (serviceMode) {
      case 'OUT_OF_ORDER':
        return 'border-red-500 bg-red-900/30';
      case 'CHECKOUT_TODAY':
        return 'border-orange-500 bg-orange-900/30';
      case 'NO_SERVICE':
        // Green Stay eco-skip = green; already-clean room with no task = neutral slate
        return source === 'GREEN_STAY'
          ? 'border-emerald-500 bg-emerald-900/30'
          : 'border-slate-600 bg-slate-800/60';
      case 'TOWELS_ONLY':
        return 'border-amber-500 bg-amber-900/30';
      case 'FULL_CLEAN':
      default:
        return 'border-blue-500 bg-blue-900/30';
    }
  };

  const getServiceLabel = () => {
    // TOMORROW: Show eco preferences for planning (not dirty state)
    if (isTomorrow) {
      switch (serviceMode) {
        case 'OUT_OF_ORDER':
          return 'OOO';
        case 'CHECKOUT_TODAY':
          return 'C/O Clean';
        case 'NO_SERVICE':
          return source === 'GREEN_STAY' ? 'Skip' : 'Clean';
        case 'TOWELS_ONLY':
          return 'Towels';
        case 'FULL_CLEAN':
        default:
          return 'Full Clean';
      }
    }
    // TODAY: Show actual dirty/cleaning state
    switch (serviceMode) {
      case 'OUT_OF_ORDER':
        return 'OOO';
      case 'CHECKOUT_TODAY':
        return 'C/O Today';
      case 'NO_SERVICE':
        return source === 'GREEN_STAY' ? 'Skip' : 'Clean';
      case 'TOWELS_ONLY':
        return 'Towels';
      case 'FULL_CLEAN':
      default:
        return source === 'GREEN_STAY' ? 'Clean' : 'Dirty';
    }
  };

  const getSourceIcon = () => {
    if (serviceMode === 'CHECKOUT_TODAY') return '🚪';
    if (!source) return '';
    switch (source) {
      case 'MAINTENANCE':
        return '🔧';
      case 'GREEN_STAY':
        return '🌿';
      case 'GUEST_REQUEST':
        return '📋';
      default:
        return '';
    }
  };

  return (
    <div
      className={`w-full aspect-square p-2 flex flex-col items-center justify-center gap-1 text-xs font-medium border-2 rounded hover:border-slate-400 transition-all cursor-pointer ${
        isSelected ? 'ring-2 ring-blue-500 border-blue-500 scale-105' : getServiceColor()
      }`}
      title={notes || getServiceLabel()}
      onClick={onClick}
    >
      <span className="text-slate-100 font-semibold">{room}</span>
      <span className={`text-[10px] ${serviceMode === 'CHECKOUT_TODAY' ? 'text-orange-400 font-semibold' : 'text-slate-300'}`}>{getServiceLabel()}</span>
      {source && <span className="text-xs">{getSourceIcon()}</span>}
    </div>
  );
}

interface PlaceholderContentProps {
  children: React.ReactNode;
}

function PlaceholderContent({ children }: PlaceholderContentProps) {
  return (
    <div className="h-32 flex items-center justify-center text-slate-500 text-sm">
      {children}
    </div>
  );
}
